// Minimal client-side i18n helper.
// Language resolution order:
//   1. explicit localStorage("mms.lang")  ("it" | "en")
//   2. navigator.language starts with "it" -> "it"
//   3. fallback "en"
// Extend the dictionaries below or plug a real i18n library later.
// The API is intentionally tiny: `const t = useT()` then `t("clients.new")`.

import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

const DICT = {
  en: {
    "common.new": "New",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.search": "Search...",
    "common.filters": "Filters",
    "empty.cta_first": "Create your first entry",

    "clients.title": "Clients",
    "clients.eyebrow": "Brands & CRM",
    "clients.desc": "Every brand, atelier and buyer served by the studio.",
    "clients.new": "New Client",
    "clients.empty": "No clients yet",
    "clients.empty_hint": "Add your first client to get started.",
    "clients.empty_hint_ro": "No clients assigned.",
    "clients.cta_first": "Add first client",
    "collections.new": "New Collection",
    "collections.empty": "No collections yet",
    "collections.cta_first_coll": "Create first collection",

    "coll.field.name": "Name",
    "coll.field.season": "Season",
    "coll.field.year": "Year",
    "coll.field.client": "Client",
    "coll.field.status": "Status",
    "coll.field.description": "Description",
    "coll.field.cover": "Cover image",
    "coll.card.garments": "garments",
    "coll.card.progress": "Progress",
    "coll.filter.all_status": "All statuses",
    "coll.filter.include_archived": "Show archived",
    "coll.action.archive": "Archive",
    "coll.action.unarchive": "Reactivate",
    "coll.confirm.archive": "Archive this collection? You can restore it later.",
    "coll.modal.new": "New collection",
    "coll.modal.edit": "Edit collection",
    "coll.modal.save": "Save",
    "coll.modal.cancel": "Cancel",

    "garment.section.main": "Main information",
    "garment.section.variants": "Variants & materials",
    "garment.section.measurements": "Sizes & measurements",
    "garment.section.files": "Images, files & notes",
    "garment.field.code": "Garment code",
    "garment.field.name": "Name",
    "garment.field.category": "Category",
    "garment.field.gender": "Gender",
    "garment.field.season": "Season",
    "garment.field.year": "Year",
    "garment.field.client": "Client",
    "garment.field.collection": "Collection",
    "garment.field.dev_phase": "Development phase",
    "garment.field.cost": "Cost €",
    "garment.field.retail": "Retail €",
    "garment.field.sizes": "Sizes",
    "garment.field.colors": "Colours",
    "garment.field.trims": "Trims",
    "garment.field.materials": "Materials",
    "garment.field.tech_notes": "Technical notes",
    "garment.field.seamstress_notes": "Notes for the seamstress",
    "garment.tbl.image": "Image",
    "garment.tbl.code": "Code",
    "garment.tbl.name": "Name",
    "garment.tbl.category": "Category",
    "garment.tbl.collection": "Collection",
    "garment.tbl.sizes_colors": "Sizes / Colours",
    "garment.tbl.phase": "Phase",
    "garment.filter.all_categories": "All categories",
    "garment.filter.all_phases": "All phases",
    "garment.filter.all_clients": "All clients",
    "garment.filter.all_collections": "All collections",
    "garment.err.code_required": "Code is required",
    "garment.err.code_duplicate": "Code already used for another garment",
    "garment.chip.add": "Add and press Enter",
    "garment.add_material": "Add material",
    "garment.add_line": "Add",
    "garment.form.close": "Close",
    "garment.form.save": "Save garment",

    "client.section.identity": "Identity",
    "client.section.contacts": "Contacts",
    "client.section.addresses": "Addresses",
    "client.section.preferences": "Preferences",
    "client.field.client_type": "Client type",
    "client.field.name": "Name or company name",
    "client.field.contact_name": "Contact person",
    "client.field.brand": "Client company / brand",
    "client.field.email": "Email",
    "client.field.phone": "Phone",
    "client.field.website": "Website",
    "client.field.vat": "VAT / Tax ID",
    "client.field.country": "Country",
    "client.field.city": "City",
    "client.field.billing_address": "Billing address",
    "client.field.shipping_address": "Shipping address",
    "client.field.same_shipping": "Same as billing address",
    "client.field.preferred_language": "Preferred language",
    "client.field.currency": "Currency",
    "client.field.tags": "Tags",
    "client.field.notes": "Notes",
    "client.field.status": "Status",
    "client.field.source": "Source",
    "client.addr.line1": "Address line 1",
    "client.addr.line2": "Address line 2",
    "client.addr.zip": "ZIP",
    "client.addr.city": "City",
    "client.addr.province": "Province",
    "client.addr.country": "Country",
    "client.type.persona": "Person",
    "client.type.azienda": "Company",
    "client.type.rivenditore": "Reseller",
    "client.status.attivo": "Active",
    "client.status.archiviato": "Archived",
    "client.source.manuale": "Manual",
    "client.source.altro": "Other",
    "client.modal.new": "New client",
    "client.modal.edit": "Edit client",
    "client.modal.save": "Save client",
    "client.modal.cancel": "Cancel",
    "client.filter.all_types": "All types",
    "client.filter.all_sources": "All sources",
    "client.filter.all_statuses": "All statuses",
    "client.filter.include_archived": "Show archived",
    "client.action.archive": "Archive",
    "client.action.unarchive": "Reactivate",
    "client.confirm.archive": "Archive this client? You can restore it later.",
    "client.tbl.name": "Name",
    "client.tbl.contact": "Contact",
    "client.tbl.email": "Email",
    "client.tbl.phone": "Phone",
    "client.tbl.location": "City / Country",
    "client.tbl.type": "Type",
    "client.tbl.source": "Source",
    "client.tbl.status": "Status",
    "client.err.required": "This field is required",
    "client.err.email_invalid": "Please enter a valid email address",
    "client.hint.required": "Required",
    "client.hint.country_search": "Search country...",

    "order.title": "Orders",
    "order.new": "New Order",
    "order.empty": "No orders yet",
    "order.cta_first": "Create first order",
    "order.section.summary": "Summary",
    "order.section.lines": "Order lines",
    "order.section.shipping": "Shipping",
    "order.section.notes": "Notes",
    "order.field.order_number": "Order number",
    "order.field.order_type": "Order type",
    "order.field.source": "Source",
    "order.field.client": "Client",
    "order.field.collection": "Collection",
    "order.field.status": "Status",
    "order.field.payment_status": "Payment status",
    "order.field.order_date": "Order date",
    "order.field.delivery_date": "Delivery date",
    "order.field.currency": "Currency",
    "order.field.tax_rate": "VAT %",
    "order.field.notes": "Notes",
    "order.line.product": "Product / Garment",
    "order.line.sku": "SKU / Variant",
    "order.line.color": "Colour",
    "order.line.size": "Size",
    "order.line.quantity": "Qty",
    "order.line.unit_price": "Unit price",
    "order.line.discount": "Discount %",
    "order.line.tax": "VAT %",
    "order.line.line_total": "Line total",
    "order.totals.subtotal": "Subtotal",
    "order.totals.discount": "Discount",
    "order.totals.tax": "VAT",
    "order.totals.total": "Total",
    "order.add_line": "Add line",
    "order.filter.all_types": "All types",
    "order.filter.all_sources": "All sources",
    "order.filter.all_statuses": "All statuses",
    "order.filter.all_pay": "All payments",
    "order.filter.include_archived": "Show archived",
    "order.modal.new": "New order",
    "order.modal.edit": "Edit order",
    "order.modal.save": "Save order",
    "order.modal.cancel": "Cancel",
    "order.action.archive": "Archive",
    "order.action.unarchive": "Reactivate",
    "order.confirm.archive": "Archive this order? You can restore it later.",
    "order.tbl.number": "Number",
    "order.tbl.client": "Client",
    "order.tbl.type": "Type",
    "order.tbl.source": "Source",
    "order.tbl.date": "Date",
    "order.tbl.delivery": "Delivery",
    "order.tbl.total": "Total",
    "order.tbl.payment": "Payment",
    "order.tbl.status": "Status",
    "order.err.client_required": "Please pick a client",
    "order.err.line_required": "Add at least one order line",
    "order.new_client_hint": "New client...",

    "orders.title": "Orders",
    "orders.eyebrow": "Sales & Invoicing",
    "orders.desc": "Production commissions and invoices.",
    "orders.new": "New Order",
    "orders.empty": "No orders yet",
    "orders.cta_first": "Create first order",

    "collections.title": "Collections",
    "collections.eyebrow": "Seasonal Lines",
    "collections.desc": "Seasonal collections and linked garments.",
    "collections.new": "New Collection",
    "collections.empty": "No collections yet",
    "collections.cta_first": "Create first collection",

    "garments.title": "Garments",
    "garments.eyebrow": "Catalogue",
    "garments.desc": "Garment catalogue with tech packs and images.",
    "garments.new": "New Garment",
    "garments.empty": "No garments yet",
    "garments.cta_first": "Create first garment",

    "materials.title": "Materials",
    "materials.eyebrow": "Fabrics & Trims",
    "materials.desc": "Fabrics, trims and composition data.",
    "materials.new": "New Material",
    "materials.empty": "No materials yet",
    "materials.cta_first": "Add first material",

    "inventory.title": "Inventory",
    "inventory.eyebrow": "Warehouse",
    "inventory.desc": "On-hand stock and full movement ledger.",
    "inventory.new_movement": "Record Movement",
    "inventory.cta_first": "Record first movement",

    "clients_orders.eyebrow": "Clients & Orders",
    "collections_garments.eyebrow": "Collections & Garments",
    "materials_inventory.eyebrow": "Materials & Inventory",
    "tab.clients": "Clients",
    "tab.orders": "Orders",
    "tab.collections": "Collections",
    "tab.garments": "Garments",
    "tab.materials": "Materials",
    "tab.inventory": "Inventory",
    "tab.finished": "Finished goods",
    "tab.needs": "Requirements",
    "tab.receipts": "Deliveries",
    "tab.purchases": "Purchase orders",
    "nav.productions": "Production",

    "nav.dashboard": "Dashboard",
    "nav.suppliers": "Suppliers",
    "nav.tasks": "Tasks",
    "nav.folder": "Brand Folder",
    "nav.chat": "Team Chat",
    "nav.ai": "AI Assistant",
    "nav.mms": "MMS Price Simulator",

    "tasks.eyebrow": "Production Floor",
    "tasks.title": "Tasks & Calendar",
    "tasks.desc": "Multi-day tasks are auto-distributed across working days based on operator capacity.",
    "tasks.new": "New Task",
    "tasks.empty": "No tasks match your filters",
    "tasks.summary.overdue": "Overdue",
    "tasks.summary.today": "Today",
    "tasks.summary.blocked": "Blocked",
    "tasks.summary.capacity": "Weekly capacity",
    "tasks.view.board": "Board",
    "tasks.view.month": "Month",
    "tasks.view.week": "Week",
    "tasks.view.gantt": "Gantt",
    "tasks.view.list": "List",
    "tasks.filter.search": "Search tasks...",
    "tasks.filter.all_assignees": "All assignees",
    "tasks.filter.all_collections": "All collections",
    "tasks.filter.all_garments": "All garments",
    "tasks.filter.any_status": "Any status",
    "tasks.filter.any_priority": "Any priority",
    "tasks.col.task": "Task",
    "tasks.col.client": "Client",
    "tasks.col.collection_garment": "Collection / Garment",
    "tasks.col.assignee": "Assignee",
    "tasks.col.dates": "Dates",
    "tasks.col.hours": "Hours",
    "tasks.col.status": "Status",
    "tasks.col.priority": "Priority",
    "tasks.form.section.task": "Task",
    "tasks.form.section.link": "Collection / Garment",
    "tasks.form.section.assignee": "Assignee",
    "tasks.form.section.dates": "Dates",
    "tasks.form.section.hours": "Hours",
    "tasks.form.section.meta": "Priority & Status",
    "tasks.form.section.description": "Description",
    "tasks.form.title": "Title",
    "tasks.form.collection": "Collection",
    "tasks.form.garment": "Garment",
    "tasks.form.assignee": "Assignee",
    "tasks.form.start": "Start date",
    "tasks.form.hours_required": "Hours required",
    "tasks.form.hours_per_day": "Hours per day",
    "tasks.form.priority": "Priority",
    "tasks.form.status": "Status",
    "tasks.form.description": "Description",
    "tasks.form.preview": "Preview Schedule",
    "tasks.form.preview_result": "working days, ends",
    "tasks.form.new": "New task",
    "tasks.form.edit": "Edit task",
    "tasks.week.of": "Week of",
    "tasks.week.member": "Team member",
    "tasks.week.capacity_over": "Over capacity",
    "tasks.status.todo": "To do",
    "tasks.status.in_progress": "In progress",
    "tasks.status.done": "Done",
    "tasks.status.blocked": "Blocked",
    "tasks.priority.low": "Low",
    "tasks.priority.normal": "Normal",
    "tasks.priority.high": "High",
    "tasks.priority.urgent": "Urgent",
    "tasks.unassigned": "Unassigned",
    "tasks.today": "Today",
    "tasks.confirm_delete": "Delete this task?",
  },
  it: {
    "common.new": "Nuovo",
    "common.save": "Salva",
    "common.cancel": "Annulla",
    "common.edit": "Modifica",
    "common.delete": "Elimina",
    "common.search": "Cerca...",
    "common.filters": "Filtri",
    "empty.cta_first": "Crea il primo elemento",

    "clients.title": "Clienti",
    "clients.eyebrow": "Brand & CRM",
    "clients.desc": "Anagrafica clienti e commesse in un'unica vista.",
    "clients.new": "Nuovo cliente",
    "clients.empty": "Nessun cliente presente",
    "clients.empty_hint": "Aggiungi il primo cliente per iniziare.",
    "clients.empty_hint_ro": "Nessun cliente assegnato.",
    "clients.cta_first": "Aggiungi il primo cliente",

    "client.section.identity": "Anagrafica",
    "client.section.contacts": "Contatti",
    "client.section.addresses": "Indirizzi",
    "client.section.preferences": "Preferenze",
    "client.field.client_type": "Tipologia cliente",
    "client.field.name": "Nome o ragione sociale",
    "client.field.contact_name": "Nome del referente",
    "client.field.brand": "Azienda / Brand del cliente",
    "client.field.email": "Email",
    "client.field.phone": "Telefono",
    "client.field.website": "Sito web",
    "client.field.vat": "Partita IVA / Codice fiscale",
    "client.field.country": "Paese",
    "client.field.city": "Città",
    "client.field.billing_address": "Indirizzo di fatturazione",
    "client.field.shipping_address": "Indirizzo di spedizione",
    "client.field.same_shipping": "Uguale all'indirizzo di fatturazione",
    "client.field.preferred_language": "Lingua preferita",
    "client.field.currency": "Valuta",
    "client.field.tags": "Tag",
    "client.field.notes": "Note",
    "client.field.status": "Stato",
    "client.field.source": "Origine",
    "client.addr.line1": "Indirizzo",
    "client.addr.line2": "Indirizzo (riga 2)",
    "client.addr.zip": "CAP",
    "client.addr.city": "Città",
    "client.addr.province": "Provincia",
    "client.addr.country": "Paese",
    "client.type.persona": "Persona",
    "client.type.azienda": "Azienda",
    "client.type.rivenditore": "Rivenditore",
    "client.status.attivo": "Attivo",
    "client.status.archiviato": "Archiviato",
    "client.source.manuale": "Manuale",
    "client.source.altro": "Altro",
    "client.modal.new": "Nuovo cliente",
    "client.modal.edit": "Modifica cliente",
    "client.modal.save": "Salva cliente",
    "client.modal.cancel": "Annulla",
    "client.filter.all_types": "Tutte le tipologie",
    "client.filter.all_sources": "Tutte le origini",
    "client.filter.all_statuses": "Tutti gli stati",
    "client.filter.include_archived": "Mostra archiviati",
    "client.action.archive": "Archivia",
    "client.action.unarchive": "Riattiva",
    "client.confirm.archive": "Archiviare questo cliente? Potrai ripristinarlo in seguito.",
    "client.tbl.name": "Nome",
    "client.tbl.contact": "Referente",
    "client.tbl.email": "Email",
    "client.tbl.phone": "Telefono",
    "client.tbl.location": "Città / Paese",
    "client.tbl.type": "Tipologia",
    "client.tbl.source": "Origine",
    "client.tbl.status": "Stato",
    "client.err.required": "Campo obbligatorio",
    "client.err.email_invalid": "Inserisci un indirizzo email valido",
    "client.hint.required": "Obbligatorio",
    "client.hint.country_search": "Cerca paese...",

    "order.title": "Ordini",
    "order.new": "Nuovo ordine",
    "order.empty": "Nessun ordine presente",
    "order.cta_first": "Crea il primo ordine",
    "order.section.summary": "Riepilogo",
    "order.section.lines": "Righe ordine",
    "order.section.shipping": "Spedizione",
    "order.section.notes": "Note",
    "order.field.order_number": "Numero ordine",
    "order.field.order_type": "Tipo",
    "order.field.source": "Origine",
    "order.field.client": "Cliente",
    "order.field.collection": "Collezione",
    "order.field.status": "Stato",
    "order.field.payment_status": "Stato pagamento",
    "order.field.order_date": "Data ordine",
    "order.field.delivery_date": "Data consegna",
    "order.field.currency": "Valuta",
    "order.field.tax_rate": "IVA %",
    "order.field.notes": "Note",
    "order.line.product": "Prodotto / Capo",
    "order.line.sku": "SKU / Variante",
    "order.line.color": "Colore",
    "order.line.size": "Taglia",
    "order.line.quantity": "Q.tà",
    "order.line.unit_price": "Prezzo unit.",
    "order.line.discount": "Sconto %",
    "order.line.tax": "IVA %",
    "order.line.line_total": "Totale riga",
    "order.totals.subtotal": "Imponibile",
    "order.totals.discount": "Sconto",
    "order.totals.tax": "IVA",
    "order.totals.total": "Totale",
    "order.add_line": "Aggiungi riga",
    "order.filter.all_types": "Tutti i tipi",
    "order.filter.all_sources": "Tutte le origini",
    "order.filter.all_statuses": "Tutti gli stati",
    "order.filter.all_pay": "Tutti i pagamenti",
    "order.filter.include_archived": "Mostra archiviati",
    "order.modal.new": "Nuovo ordine",
    "order.modal.edit": "Modifica ordine",
    "order.modal.save": "Salva ordine",
    "order.modal.cancel": "Annulla",
    "order.action.archive": "Archivia",
    "order.action.unarchive": "Riattiva",
    "order.confirm.archive": "Archiviare questo ordine? Potrai ripristinarlo in seguito.",
    "order.tbl.number": "Numero",
    "order.tbl.client": "Cliente",
    "order.tbl.type": "Tipo",
    "order.tbl.source": "Origine",
    "order.tbl.date": "Data",
    "order.tbl.delivery": "Consegna",
    "order.tbl.total": "Totale",
    "order.tbl.payment": "Pagamento",
    "order.tbl.status": "Stato",
    "order.err.client_required": "Seleziona un cliente",
    "order.err.line_required": "Aggiungi almeno una riga",
    "order.new_client_hint": "Nuovo cliente...",

    "orders.title": "Ordini",
    "orders.eyebrow": "Vendite & Fatturazione",
    "orders.desc": "Visualizza e gestisci gli ordini di produzione dei tuoi clienti.",
    "orders.new": "Nuovo ordine",
    "orders.empty": "Nessun ordine presente",
    "orders.cta_first": "Crea il primo ordine",

    "collections.title": "Collezioni",
    "collections.eyebrow": "Linee stagionali",
    "collections.desc": "Collezioni stagionali con i capi collegati.",
    "collections.new": "Nuova collezione",
    "collections.empty": "Nessuna collezione presente",
    "collections.cta_first": "Crea la prima collezione",
    "collections.cta_first_coll": "Crea la prima collezione",

    "coll.field.name": "Nome",
    "coll.field.season": "Stagione",
    "coll.field.year": "Anno",
    "coll.field.client": "Cliente",
    "coll.field.status": "Stato",
    "coll.field.description": "Descrizione",
    "coll.field.cover": "Immagine di copertina",
    "coll.card.garments": "capi",
    "coll.card.progress": "Avanzamento",
    "coll.filter.all_status": "Tutti gli stati",
    "coll.filter.include_archived": "Mostra archiviate",
    "coll.action.archive": "Archivia",
    "coll.action.unarchive": "Riattiva",
    "coll.confirm.archive": "Archiviare questa collezione? Potrai ripristinarla in seguito.",
    "coll.modal.new": "Nuova collezione",
    "coll.modal.edit": "Modifica collezione",
    "coll.modal.save": "Salva",
    "coll.modal.cancel": "Annulla",

    "garment.section.main": "Informazioni principali",
    "garment.section.variants": "Varianti e materiali",
    "garment.section.measurements": "Misure e sviluppo taglie",
    "garment.section.files": "Immagini, documenti e note tecniche",
    "garment.field.code": "Codice capo",
    "garment.field.name": "Nome",
    "garment.field.category": "Categoria",
    "garment.field.gender": "Genere",
    "garment.field.season": "Stagione",
    "garment.field.year": "Anno",
    "garment.field.client": "Cliente",
    "garment.field.collection": "Collezione",
    "garment.field.dev_phase": "Fase di sviluppo",
    "garment.field.cost": "Costo €",
    "garment.field.retail": "Prezzo di vendita €",
    "garment.field.sizes": "Taglie",
    "garment.field.colors": "Colori",
    "garment.field.trims": "Accessori",
    "garment.field.materials": "Materiali",
    "garment.field.tech_notes": "Note tecniche",
    "garment.field.seamstress_notes": "Note per la sarta",
    "garment.tbl.image": "Immagine",
    "garment.tbl.code": "Codice",
    "garment.tbl.name": "Nome",
    "garment.tbl.category": "Categoria",
    "garment.tbl.collection": "Collezione",
    "garment.tbl.sizes_colors": "Taglie / Colori",
    "garment.tbl.phase": "Fase",
    "garment.filter.all_categories": "Tutte le categorie",
    "garment.filter.all_phases": "Tutte le fasi",
    "garment.filter.all_clients": "Tutti i clienti",
    "garment.filter.all_collections": "Tutte le collezioni",
    "garment.err.code_required": "Codice obbligatorio",
    "garment.err.code_duplicate": "Codice già usato per un altro capo",
    "garment.chip.add": "Aggiungi e premi Invio",
    "garment.add_material": "Aggiungi materiale",
    "garment.add_line": "Aggiungi",
    "garment.form.close": "Chiudi",
    "garment.form.save": "Salva capo",

    "garments.title": "Capi",
    "garments.eyebrow": "Catalogo",
    "garments.desc": "Catalogo dei capi prodotti, con schede tecniche e immagini.",
    "garments.new": "Nuovo capo",
    "garments.empty": "Nessun capo presente",
    "garments.cta_first": "Crea il primo capo",

    "materials.title": "Materiali",
    "materials.eyebrow": "Tessuti & Accessori",
    "materials.desc": "Anagrafica materiali con disponibilità e composizione.",
    "materials.new": "Nuovo materiale",
    "materials.empty": "Nessun materiale presente",
    "materials.cta_first": "Aggiungi il primo materiale",

    "inventory.title": "Magazzino",
    "inventory.eyebrow": "Magazzino",
    "inventory.desc": "Movimenti di carico/scarico e giacenze attuali.",
    "inventory.new_movement": "Registra movimento",
    "inventory.cta_first": "Registra il primo movimento",

    "clients_orders.eyebrow": "Clienti & Ordini",
    "collections_garments.eyebrow": "Collezioni & Capi",
    "materials_inventory.eyebrow": "Materiali & Magazzino",
    "tab.clients": "Clienti",
    "tab.orders": "Ordini",
    "tab.collections": "Collezioni",
    "tab.garments": "Capi",
    "tab.materials": "Materiali",
    "tab.inventory": "Magazzino",
    "tab.finished": "Prodotti finiti",
    "tab.needs": "Fabbisogno",
    "tab.receipts": "Arrivi",
    "tab.purchases": "Ordini fornitori",
    "nav.productions": "Produzione",

    "nav.dashboard": "Dashboard",
    "nav.suppliers": "Fornitori",
    "nav.tasks": "Attività",
    "nav.folder": "Cartella Brand",
    "nav.chat": "Chat del Team",
    "nav.ai": "Assistente AI",
    "nav.mms": "Simulatore Prezzi MMS",

    "tasks.eyebrow": "Produzione",
    "tasks.title": "Attività & Calendario",
    "tasks.desc": "Le attività su più giorni vengono distribuite automaticamente in base alla capacità degli operatori.",
    "tasks.new": "Nuova attività",
    "tasks.empty": "Nessuna attività corrisponde ai filtri",
    "tasks.summary.overdue": "Scadute",
    "tasks.summary.today": "Oggi",
    "tasks.summary.blocked": "Bloccate",
    "tasks.summary.capacity": "Capacità settimanale",
    "tasks.view.board": "Board",
    "tasks.view.month": "Mese",
    "tasks.view.week": "Settimana",
    "tasks.view.gantt": "Gantt",
    "tasks.view.list": "Lista",
    "tasks.filter.search": "Cerca attività...",
    "tasks.filter.all_assignees": "Tutti gli assegnatari",
    "tasks.filter.all_collections": "Tutte le collezioni",
    "tasks.filter.all_garments": "Tutti i capi",
    "tasks.filter.any_status": "Qualsiasi stato",
    "tasks.filter.any_priority": "Qualsiasi priorità",
    "tasks.col.task": "Attività",
    "tasks.col.client": "Cliente",
    "tasks.col.collection_garment": "Collezione / Capo",
    "tasks.col.assignee": "Assegnatario",
    "tasks.col.dates": "Date",
    "tasks.col.hours": "Ore",
    "tasks.col.status": "Stato",
    "tasks.col.priority": "Priorità",
    "tasks.form.section.task": "Attività",
    "tasks.form.section.link": "Collezione / Capo",
    "tasks.form.section.assignee": "Assegnatario",
    "tasks.form.section.dates": "Date",
    "tasks.form.section.hours": "Ore necessarie",
    "tasks.form.section.meta": "Priorità & Stato",
    "tasks.form.section.description": "Descrizione",
    "tasks.form.title": "Titolo",
    "tasks.form.collection": "Collezione",
    "tasks.form.garment": "Capo",
    "tasks.form.assignee": "Assegnatario",
    "tasks.form.start": "Data inizio",
    "tasks.form.hours_required": "Ore necessarie",
    "tasks.form.hours_per_day": "Ore al giorno",
    "tasks.form.priority": "Priorità",
    "tasks.form.status": "Stato",
    "tasks.form.description": "Descrizione",
    "tasks.form.preview": "Anteprima pianificazione",
    "tasks.form.preview_result": "giorni lavorativi, termina il",
    "tasks.form.new": "Nuova attività",
    "tasks.form.edit": "Modifica attività",
    "tasks.week.of": "Settimana del",
    "tasks.week.member": "Membro del team",
    "tasks.week.capacity_over": "Fuori capacità",
    "tasks.status.todo": "Da fare",
    "tasks.status.in_progress": "In corso",
    "tasks.status.done": "Completata",
    "tasks.status.blocked": "Bloccata",
    "tasks.priority.low": "Bassa",
    "tasks.priority.normal": "Normale",
    "tasks.priority.high": "Alta",
    "tasks.priority.urgent": "Urgente",
    "tasks.unassigned": "Non assegnata",
    "tasks.today": "Oggi",
    "tasks.confirm_delete": "Eliminare questa attività?",
  },
};

function detectLang() {
  if (typeof window === "undefined") return "it";
  const stored = window.localStorage?.getItem("mms.lang");
  if (stored === "it" || stored === "en") return stored;
  const nav = (window.navigator?.language || "").toLowerCase();
  // App is primarily Italian; default to it unless the browser is explicitly English.
  return nav.startsWith("en") ? "en" : "it";
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  useEffect(() => {
    const onStorage = (e) => { if (e.key === "mms.lang") setLangState(detectLang()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setLang = useCallback((l) => {
    window.localStorage.setItem("mms.lang", l);
    setLangState(l);
  }, []);

  const t = useCallback((key, fallback) => {
    const table = DICT[lang] || DICT.en;
    return table[key] ?? DICT.en[key] ?? fallback ?? key;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) return { lang: detectLang(), setLang: () => {}, t: (k, f) => (DICT[detectLang()] || DICT.en)[k] ?? DICT.en[k] ?? f ?? k };
  return ctx;
}

/** Convenience: `const t = useT()` -> pure translator */
export function useT() { return useI18n().t; }
